import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { admin, type AdminClub } from '../../api/admin'
import { ApiError } from '../../api/client'
import { ClubMark } from '../../components/ClubMark'
import {
  activityTone,
  clubAlerts,
  clubSeverity,
  plural,
  relativeDay,
  seatUsage,
  serviceStatus,
  tagClass,
} from './clubOps'

/**
 * `/admin/clubs` — la vista de operación de los clubes, no un directorio.
 *
 * mg (2026-09-08): *"no hay ningún lugar donde se vean los clubes: cuáles
 * hay, sus configs, su info"*. Hasta esa fecha la página listaba nombre,
 * logo y jugadores, y encima no estaba en la barra del admin (se llegaba por
 * un botón en `/admin/users`). Ahora cada fila responde, sin entrar a la
 * ficha, lo que un operador necesita saber del club: división, licencias
 * usadas sobre el cupo, vencimiento del servicio, staff (y cuántos deben los
 * T&C), códigos de club (la credencial de tablets y visores) y actividad reciente.
 *
 * Arriba va una banda de totales y, cuando hay algo que resolver, el bloque
 * "Requieren atención": la lista completa se lee cuando alcanza con mirarla,
 * pero lo urgente no depende de que alguien escanee la tabla.
 */
export function AdminClubs() {
  const [clubs, setClubs] = useState<AdminClub[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')

  async function reload() {
    setError(null)
    try {
      const r = await admin.clubs()
      setClubs(r.clubs)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  async function createClub(e: React.FormEvent) {
    e.preventDefault()
    const clean = newName.trim()
    if (!clean) return
    setCreating(true)
    setError(null)
    try {
      await admin.createClub(clean)
      setNewName('')
      await reload()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    } finally {
      setCreating(false)
    }
  }

  const totals = useMemo(() => {
    if (!clubs) return null
    return {
      clubs: clubs.length,
      players: clubs.reduce((n, c) => n + c.player_count, 0),
      expired: clubs.filter((c) => !c.service_active).length,
      kits: clubs.reduce((n, c) => n + c.kit_count, 0),
      active7d: clubs.filter((c) => c.sessions_7d > 0).length,
      sessions7d: clubs.reduce((n, c) => n + c.sessions_7d, 0),
    }
  }, [clubs])

  // Lo que ya está roto arriba de lo que todavía tiene margen; a igual
  // severidad se respeta el orden alfabético que manda el backend.
  const needsAttention = useMemo(
    () =>
      (clubs ?? [])
        .map((c) => ({ club: c, alerts: clubAlerts(c), severity: clubSeverity(c) }))
        .filter((x) => x.alerts.length > 0)
        .sort((a, b) => b.severity - a.severity),
    [clubs],
  )

  return (
    <section>
      <div className="page-header">
        <h1>Clubes</h1>
        <p className="text-sm text-text-muted">
          {clubs ? `${clubs.length} club${clubs.length === 1 ? '' : 'es'}` : ''}
        </p>
      </div>

      {error && <div className="banner-err mb-md">{error}</div>}

      {totals && (
        <div className="mb-lg grid grid-cols-2 gap-md sm:grid-cols-3 lg:grid-cols-6">
          <Stat label="Clubes" value={totals.clubs} />
          <Stat label="Jugadores" value={totals.players} />
          <Stat label="Códigos de club" value={totals.kits} />
          <Stat label="Activos (7d)" value={totals.active7d} />
          <Stat label="Sesiones (7d)" value={totals.sessions7d} />
          <Stat label="Servicio vencido" value={totals.expired} tone={totals.expired > 0 ? 'err' : 'ok'} />
        </div>
      )}

      {needsAttention.length > 0 && (
        <div className="card mb-lg">
          <h2 className="mt-0 text-base">Requieren atención</h2>
          <ul className="m-0 flex list-none flex-col gap-sm p-0">
            {needsAttention.map(({ club, alerts, severity }) => (
              <li key={club.id} className="flex flex-col gap-xs text-sm sm:flex-row sm:gap-md">
                <Link
                  to={`/admin/clubs/${club.id}`}
                  className={`shrink-0 font-medium underline-offset-2 hover:underline sm:w-48 ${severity === 2 ? 'text-danger' : ''}`}
                >
                  {club.name}
                </Link>
                <span className="text-text-secondary">{alerts.join(' ')}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={createClub} className="filter-bar">
        <input
          type="text"
          placeholder="Nombre del club"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          className="input w-72"
        />
        <button type="submit" className="btn-primary" disabled={creating || !newName.trim()}>
          {creating ? 'Creando…' : '+ Crear club'}
        </button>
        <span className="text-xs text-text-muted">
          Se crea sin cupo ni vencimiento; la división, las licencias y el
          servicio se cargan en la ficha del club.
        </span>
      </form>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Club</th>
              <th>Licencias</th>
              <th>Servicio</th>
              <th>Staff</th>
              <th>Códigos de club</th>
              <th>Actividad</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {clubs === null && (
              <tr>
                <td colSpan={7} className="text-text-muted">
                  Cargando…
                </td>
              </tr>
            )}
            {clubs?.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-text-muted">
                  Sin clubes todavía.
                </td>
              </tr>
            )}
            {clubs?.map((c) => (
              <ClubRow key={c.id} club={c} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

/** Exportada para el test de render: la fila es el entregable de esta
 * página (lo que el operador lee sin entrar al club), así que se testea sola. */
export function ClubRow({ club }: { club: AdminClub }) {
  const seats = seatUsage(club)
  const service = serviceStatus(club)

  return (
    <tr>
      <td>
        <div className="flex items-center gap-sm">
          <ClubMark
            logoUrl={club.logo_url}
            name={club.name}
            size={32}
            radius={4}
            fallbackColor={club.primary_color}
          />
          <div className="flex flex-col leading-tight">
            <Link
              to={`/admin/clubs/${club.id}`}
              className="font-medium underline-offset-2 hover:underline"
            >
              {club.name}
            </Link>
            <span className="text-xs text-text-muted">
              {club.division || 'sin división'} · alta{' '}
              {new Date(club.created_at).toLocaleDateString('es-AR')}
            </span>
          </div>
        </div>
      </td>

      <td>
        <span className={tagClass(seats.tone)} title={seats.detail}>
          {seats.label}
        </span>
      </td>

      <td>
        <span className={tagClass(service.tone)} title={service.detail}>
          {service.label}
        </span>
      </td>

      <td>
        {club.staff_count === 0 ? (
          <span className="text-xs text-text-muted">
            {club.staff_invites_pending > 0
              ? plural(club.staff_invites_pending, 'invitación', 'invitaciones')
              : 'sin staff'}
          </span>
        ) : (
          <div className="flex flex-col leading-tight">
            <span>{club.staff_count}</span>
            {club.staff_pending_terms > 0 && (
              <span
                className="text-xs text-warning"
                title="No pueden usar el panel del club hasta aceptar los T&C."
              >
                {club.staff_pending_terms} sin T&C
              </span>
            )}
          </div>
        )}
      </td>

      <td>
        {club.kit_count === 0 ? (
          <span className="text-xs text-text-muted">sin código</span>
        ) : (
          <span>
            {club.kit_count}
            {club.kits_active < club.kit_count && (
              <span className="text-xs text-text-muted"> ({club.kits_active} activos)</span>
            )}
          </span>
        )}
      </td>

      <td>
        <div className="flex flex-col leading-tight">
          <span className={club.last_session_at ? '' : 'text-xs text-text-muted'}>
            {relativeDay(club.last_session_at)}
          </span>
          <span
            className={`text-xs ${activityTone(club) === 'ok' ? 'text-text-secondary' : 'text-text-muted'}`}
          >
            {club.sessions_7d} ses. 7d
          </span>
        </div>
      </td>

      <td className="text-right">
        <Link to={`/admin/clubs/${club.id}`} className="btn-ghost text-xs no-underline">
          Administrar
        </Link>
      </td>
    </tr>
  )
}

function Stat({
  label,
  value,
  tone = 'muted',
}: {
  label: string
  value: number
  tone?: 'muted' | 'ok' | 'err'
}) {
  return (
    <div className="stat-card">
      <div className="stat-card-label">{label}</div>
      <div className={`stat-card-value ${tone === 'err' ? 'text-danger' : ''}`}>{value}</div>
    </div>
  )
}
